<template>
  <div class="event-report-tab">
    <!-- 이벤트 선택 패널 (항상 표시) -->
    <div class="event-selector-panel">
      <div class="event-selector-content">
        <div class="selector-column">
          <label class="filter-label">
            이벤트 선택
            <span v-if="eventIds" class="event-id-copy">
              <a-typography-text
                :copyable="{ text: eventIds }"
                class="event-id-text"
              >
                (ID: {{ eventIds }})
              </a-typography-text>
            </span>
          </label>
          <a-select
            v-model:value="eventIds"
            placeholder="이벤트를 선택해주세요"
            class="event-selector"
            :loading="loadingEvents"
            @change="handleEventChange"
            allow-clear
            showSearch
            :filter-option="filterOption"
          >
            <a-select-option
              v-for="event in eventList"
              :key="event._id"
              :value="event._id"
            >
              {{ event.eventTitle }} ({{ getEventStatusText(event.eventStatus) }}, {{ event.isReportVisible ? '노출' : '미노출' }})
            </a-select-option>
          </a-select>
        </div>
        <div class="selector-actions">
          <a-button @click="handleRefresh" :loading="refreshing" :disabled="!eventIds">
            <template #icon><ReloadOutlined /></template>
            새로고침
          </a-button>
        </div>
      </div>
    </div>

    <!-- 컨트롤 패널 (항상 표시) -->
    <div class="control-panel">
      <!-- 생성 영역 -->
      <div class="control-section create-section">
        <h3 class="section-title">생성</h3>
        <div class="section-content">
          <a-button type="primary" @click="handleCalculate" :loading="calculating" :disabled="!eventIds">
            <template #icon><CalculatorOutlined /></template>
            레포트 생성
          </a-button>
        </div>
      </div>

      <!-- 조회 영역 -->
      <div class="control-section">
        <h3 class="section-title">조회</h3>
        <div class="section-content">
          <a-button @click="handleRefresh" :loading="refreshing">
            <template #icon><ReloadOutlined /></template>
            새로고침
          </a-button>
        </div>
      </div>

      <!-- 이벤트 수정 영역 -->
      <div class="control-section">
        <h3 class="section-title">이벤트 관리</h3>
        <div class="section-content">
          <a-button
            type="primary"
            class="yellow-button"
            :disabled="!eventIds"
            @click="goToEditEvent"
          >
            <template #icon><i class="fa fa-edit"></i></template>
            이벤트 수정
          </a-button>
        </div>
      </div>

      <!-- 고객사 레포트 노출 설정 -->
      <div class="control-section settings-section">
        <h3 class="section-title">
          고객사 레포트 노출
          <a-tooltip title="활성화 시, 고객사 레포트 페이지의 이벤트 필터에 이 이벤트가 노출됩니다.">
            <QuestionCircleOutlined class="help-icon" />
          </a-tooltip>
        </h3>
        <div class="section-content visibility-content">
          <a-switch
            v-model:checked="isReportVisible"
            :loading="updatingVisibility"
            :disabled="!eventIds"
            @change="handleReportVisibilityChange"
          />
          <span
            class="visibility-status"
            :class="{ 'status-active': isReportVisible, 'status-inactive': !isReportVisible }"
          >
            {{ isReportVisible ? '레포트에 노출' : '레포트에 미노출' }}
          </span>
        </div>
        <div class="visibility-description" v-if="eventIds">
          <span v-if="isReportVisible" class="desc-active">
            고객사 레포트 페이지에서 이 이벤트를 선택할 수 있습니다.
          </span>
          <span v-else class="desc-inactive">
            고객사 레포트 페이지의 이벤트 필터에 이 이벤트가 표시되지 않습니다.
          </span>
        </div>
        <div class="visibility-description" v-else>
          <span class="desc-disabled">이벤트를 선택하면 설정할 수 있습니다.</span>
        </div>
      </div>
    </div>

    <!-- 이벤트 미선택 시 안내 -->
    <div v-if="!eventIds" class="no-event-selected">
      <div class="no-event-content">
        <span class="no-event-icon">📊</span>
        <h3>이벤트를 선택해주세요</h3>
        <p>리포트를 조회할 이벤트를 상단에서 선택해주세요.</p>
      </div>
    </div>

    <!-- 로딩 상태 -->
    <div v-else-if="loading" class="loading-container">
      <a-spin size="large" tip="레포트를 불러오는 중..." />
    </div>

    <!-- 조회 결과 -->
    <a-tabs v-else-if="hasData" v-model:activeKey="activeTab" type="card" class="results-section">
      <a-tab-pane key="overview" tab="📊 전체 개요">
        <OverviewSummary
          :daily-message-conversions="reportData.dailyMessageConversions"
          :daily-conversions="reportData.dailyConversions"
          :cumulative-summaries="reportData.cumulativeSummaries"
          :overall-summary="reportData.overallSummary"
          :summary="reportData.summary"
          :report-id="reportData.reportId"
          :event-id="eventIds"
          :start-date="selectedEvent?.startDate"
          :end-date="selectedEvent?.endDate"
        />
      </a-tab-pane>

      <a-tab-pane key="messageConversion" tab="📬 메시지 유형별 전환">
        <MessageConversionReport
          :report-data="reportData.dailyMessageConversions"
          :report-id="reportData.reportId"
          :api="reportApi"
        />
      </a-tab-pane>

      <a-tab-pane key="groupAnalysis" tab="📊 그룹별 분석">
        <a-tabs v-model:activeKey="activeGroupTab" class="group-analysis-sub-tabs">
          <a-tab-pane key="groupA" tab="그룹A">
            <GroupADetails
              v-if="reportData.reportId"
              :report-id="reportData.reportId"
            />
            <div v-else class="empty-content">
              <p>보고서를 조회해주세요</p>
            </div>
          </a-tab-pane>
          <a-tab-pane key="groupB" tab="그룹B">
            <GroupBDetails
              v-if="reportData.reportId"
              :report-id="reportData.reportId"
            />
            <div v-else class="empty-content">
              <p>보고서를 조회해주세요</p>
            </div>
          </a-tab-pane>
          <a-tab-pane key="groupC" tab="그룹C">
            <GroupCDetails
              v-if="reportData.reportId"
              :report-id="reportData.reportId"
            />
            <div v-else class="empty-content">
              <p>보고서를 조회해주세요</p>
            </div>
          </a-tab-pane>
          <a-tab-pane key="groupD" tab="그룹D">
            <GroupDDetails
              v-if="reportData.reportId"
              :report-id="reportData.reportId"
            />
            <div v-else class="empty-content">
              <p>보고서를 조회해주세요</p>
            </div>
          </a-tab-pane>
        </a-tabs>
      </a-tab-pane>
    </a-tabs>

    <!-- 데이터 없음 -->
    <div v-else class="no-data">
      <div class="no-data-content">
        <span class="no-data-icon">📭</span>
        <h3>레포트 데이터가 없습니다</h3>
        <p>선택한 이벤트에 대한 레포트가 아직 생성되지 않았습니다.</p>
        <p class="hint-text">"레포트 생성" 버튼을 클릭하여 레포트를 생성해주세요.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, watch, getCurrentInstance, onMounted } from 'vue';
import { Tabs, Spin, Select, DatePicker, Button, Space, Switch, Tooltip, message } from 'ant-design-vue';
import { ReloadOutlined, CalculatorOutlined, QuestionCircleOutlined } from '@ant-design/icons-vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import dayjs from 'dayjs';
import OverviewSummary from './components/OverviewSummary.vue';
import MessageConversionReport from './components/MessageConversionReport.vue';
import GroupADetails from './components/GroupADetails.vue';
import GroupBDetails from './components/GroupBDetails.vue';
import GroupCDetails from './components/GroupCDetails.vue';
import GroupDDetails from './components/GroupDDetails.vue';
import reportApi from './api';
import eventsApi from '../../../api';

export default {
  name: 'EventReportTab',
  components: {
    ATabs: Tabs,
    ATabPane: Tabs.TabPane,
    ASpin: Spin,
    ASelect: Select,
    ASelectOption: Select.Option,
    ARangePicker: DatePicker.RangePicker,
    AButton: Button,
    ASpace: Space,
    OverviewSummary,
    MessageConversionReport,
    GroupADetails,
    GroupBDetails,
    GroupCDetails,
    GroupDDetails,
    ReloadOutlined,
    CalculatorOutlined,
    QuestionCircleOutlined,
    ASwitch: Switch,
    ATooltip: Tooltip,
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const instance = getCurrentInstance();

    // 이벤트 관련 상태
    const eventIds = ref(null);
    const eventList = ref([]);
    const loadingEvents = ref(false);
    const selectedEvent = ref(null);

    // 레포트 노출 설정
    const isReportVisible = ref(false);
    const updatingVisibility = ref(false);

    // 날짜 범위
    const dateRange = ref([dayjs().subtract(7, 'day'), dayjs()]);

    const activeTab = ref('overview');
    const activeGroupTab = ref('groupA');
    const loading = ref(false);
    const refreshing = ref(false);
    const calculating = ref(false);

    const reportData = reactive({
      dailyMessageConversions: [],
      dailyConversions: [],
      cumulativeSummaries: [],
      overallSummary: null,
      summary: null,
      reportId: null
    });

    // 데이터 존재 여부
    const hasData = computed(() => {
      return reportData.reportId !== null ||
             reportData.dailyMessageConversions.length > 0 ||
             reportData.summary !== null;
    });

    // API 인스턴스 생성
    const api = reportApi(instance.proxy.$apiInstance, store);
    const eventsApiInstance = eventsApi(instance.proxy.$apiInstance, store);

    // 날짜 포맷 헬퍼 함수
    const formatDate = (dateString) => {
      if (!dateString) return '';
      return dayjs(dateString).format('YYYY-MM-DD');
    };

    // 이벤트 상태 텍스트 변환 함수
    const getEventStatusText = (status) => {
      const statusTexts = {
        draft: '임시저장',
        preparing: '준비중',
        ongoing: '진행중',
        completed: '완료',
        paused: '일시중지',
        cancelled: '취소됨'
      };
      return statusTexts[status] || status || '알 수 없음';
    };

    // 날짜 선택 제한 함수
    const disabledDate = (current) => {
      if (!selectedEvent.value || !selectedEvent.value.startDate || !selectedEvent.value.endDate) {
        return false;
      }

      const eventStart = dayjs(selectedEvent.value.startDate).startOf('day');
      const eventEnd = dayjs(selectedEvent.value.endDate).endOf('day');

      return current && (current.isBefore(eventStart) || current.isAfter(eventEnd));
    };

    // 이벤트 목록 조회 (전체 이벤트)
    const fetchEventList = async () => {
      loadingEvents.value = true;
      try {
        await eventsApiInstance.getEventsWithStatus(
          { status: 'all', page: 1, limit: 1000 },
          (response) => {
            const events = response.data?.items || response.data?.data || [];
            eventList.value = Array.isArray(events) ? events : [];
          },
          (error) => {
            console.error('Failed to fetch event list:', error);
            message.error('이벤트 목록 조회에 실패했습니다.');
          }
        );
      } catch (error) {
        console.error('Error fetching event list:', error);
      } finally {
        loadingEvents.value = false;
      }
    };

    // 이벤트 변경 핸들러
    const handleEventChange = (value) => {
      eventIds.value = value;

      // 선택된 이벤트의 기간으로 날짜 범위 자동 변경
      if (value) {
        const event = eventList.value.find(e => e._id === value);
        if (event) {
          selectedEvent.value = event;
          // 레포트 노출 상태 동기화
          isReportVisible.value = event.isReportVisible || false;
          if (event.startDate && event.endDate) {
            dateRange.value = [
              dayjs(event.startDate),
              dayjs(event.endDate)
            ];
          }
        }
      } else {
        // 이벤트 선택 해제 시 초기화
        selectedEvent.value = null;
        isReportVisible.value = false;
      }

      // 이벤트 변경 시 자동으로 레포트 조회
      if (value) {
        fetchReportData();
      }
    };

    // 보고서 데이터 조회
    const fetchReportData = async () => {
      if (!eventIds.value) {
        return;
      }

      if (!dateRange.value || dateRange.value.length !== 2) {
        message.warning('날짜 범위를 선택해주세요.');
        return;
      }

      loading.value = true;
      refreshing.value = true;

      // 데이터 초기화
      reportData.dailyMessageConversions = [];
      reportData.dailyConversions = [];
      reportData.cumulativeSummaries = [];
      reportData.overallSummary = null;
      reportData.summary = null;
      reportData.reportId = null;

      try {
        const params = {
          eventId: eventIds.value,
          startDate: dateRange.value[0].format('YYYY-MM-DD'),
          endDate: dateRange.value[1].format('YYYY-MM-DD')
        };

        await api.getReport(
          params,
          (response) => {
            if (response.data) {
              // 레포트 ID 저장 (그룹 상세 조회에 사용)
              reportData.reportId = response.data._id || null;
              reportData.dailyMessageConversions = response.data.dailyMessageConversions || [];
              reportData.dailyConversions = response.data.dailyConversions || [];
              reportData.cumulativeSummaries = response.data.cumulativeSummaries || [];
              reportData.overallSummary = response.data.overallSummary || null;
              reportData.summary = response.data.summary || null;
            }
          },
          (error) => {
            console.error('Failed to fetch report:', error);
            // 404 에러는 데이터 없음으로 처리 (메시지 표시 안 함)
            if (error.response?.status !== 404) {
              message.error('보고서 조회에 실패했습니다.');
            }
          }
        );
      } catch (error) {
        console.error('Error fetching report:', error);
      } finally {
        loading.value = false;
        refreshing.value = false;
      }
    };

    // 보고서 계산 (재계산)
    const handleCalculate = async () => {
      if (!eventIds.value) {
        message.warning('이벤트를 선택해주세요.');
        return;
      }

      if (!dateRange.value || dateRange.value.length !== 2) {
        message.warning('날짜 범위를 선택해주세요.');
        return;
      }

      calculating.value = true;
      try {
        const params = {
          eventId: eventIds.value,
          periodType: 'custom',
          startDate: dateRange.value[0].format('YYYY-MM-DD'),
          endDate: dateRange.value[1].format('YYYY-MM-DD')
        };

        await api.calculateReport(
          params,
          (response) => {
            message.success('보고서 계산이 완료되었습니다.');
            if (response.data) {
              // 레포트 ID 저장 (그룹 상세 조회에 사용)
              reportData.reportId = response.data._id || null;
              reportData.dailyMessageConversions = response.data.dailyMessageConversions || [];
              reportData.dailyConversions = response.data.dailyConversions || [];
              reportData.cumulativeSummaries = response.data.cumulativeSummaries || [];
              reportData.overallSummary = response.data.overallSummary || null;
              reportData.summary = response.data.summary || null;
            }
          },
          (error) => {
            console.error('Failed to calculate report:', error);
            message.error('보고서 계산에 실패했습니다.');
          }
        );
      } catch (error) {
        console.error('Error calculating report:', error);
      } finally {
        calculating.value = false;
      }
    };

    // 새로고침
    const handleRefresh = () => {
      fetchReportData();
    };

    // 날짜 범위 변경 핸들러
    const handleDateRangeChange = (dates) => {
      if (dates && dates.length === 2 && eventIds.value) {
        fetchReportData();
      }
    };

    // 레포트 노출 상태 변경 핸들러
    const handleReportVisibilityChange = async (checked) => {
      if (!eventIds.value) {
        message.warning('이벤트를 먼저 선택해주세요.');
        return;
      }

      updatingVisibility.value = true;
      try {
        await eventsApiInstance.updateEvent(
          eventIds.value,
          { isReportVisible: checked },
          (response) => {
            isReportVisible.value = checked;
            // 로컬 이벤트 목록도 업데이트
            const event = eventList.value.find(e => e._id === eventIds.value);
            if (event) {
              event.isReportVisible = checked;
            }
            if (selectedEvent.value) {
              selectedEvent.value.isReportVisible = checked;
            }
            message.success(checked ? '고객사 레포트에 노출됩니다.' : '고객사 레포트에서 숨겨집니다.');
          },
          (error) => {
            console.error('레포트 노출 설정 변경 실패:', error);
            message.error('레포트 노출 설정 변경에 실패했습니다.');
            // 실패 시 원래 상태로 복원
            isReportVisible.value = !checked;
          }
        );
      } catch (error) {
        console.error('Error updating report visibility:', error);
        isReportVisible.value = !checked;
      } finally {
        updatingVisibility.value = false;
      }
    };

    // 이벤트 검색 필터 함수
    const filterOption = (input, option) => {
      return option.children[0].children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    };

    // 이벤트 수정 페이지로 이동
    const goToEditEvent = () => {
      if (!eventIds.value) return;
      router.push({
        path: `/events/${eventIds.value}/edit`,
        query: {
          returnUrl: router.currentRoute.value.fullPath
        }
      });
    };

    // 페이지 진입 시 이벤트 목록 조회 및 디폴트 이벤트 선택
    onMounted(async () => {
      await fetchEventList();

      // 이벤트 목록이 로드된 후 디폴트 이벤트 선택
      if (eventList.value.length > 0) {
        // 1. 쿼리 파라미터에 eventId가 있으면 해당 이벤트 선택
        const queryEventId = route.query.eventId;
        if (queryEventId) {
          const queryEvent = eventList.value.find(e => e._id === queryEventId);
          if (queryEvent) {
            handleEventChange(queryEventId);
            console.log('쿼리 파라미터 이벤트 자동 선택:', queryEvent.eventTitle);
            return;
          }
        }

        // 2. 쿼리 파라미터가 없으면 진행중(ongoing) 이벤트 중 가장 최신 것 선택
        const ongoingEvents = eventList.value.filter(e => e.eventStatus === 'ongoing');
        let defaultEvent;
        if (ongoingEvents.length > 0) {
          // createdAt 또는 createAt 기준 내림차순 정렬하여 가장 최신 이벤트 선택
          defaultEvent = ongoingEvents.sort((a, b) => {
            const dateA = a.createdAt || a.createAt || '';
            const dateB = b.createdAt || b.createAt || '';
            return new Date(dateB) - new Date(dateA);
          })[0];
          console.log('진행중 이벤트 중 최신 자동 선택:', defaultEvent.eventTitle);
        } else {
          // 진행중 이벤트가 없으면 첫 번째 이벤트 선택
          defaultEvent = eventList.value[0];
          console.log('첫 번째 이벤트 자동 선택:', defaultEvent.eventTitle);
        }

        handleEventChange(defaultEvent._id);
      }
    });

    return {
      eventIds,
      eventList,
      selectedEvent,
      dateRange,
      activeTab,
      activeGroupTab,
      loading,
      refreshing,
      calculating,
      loadingEvents,
      reportData,
      hasData,
      isReportVisible,
      updatingVisibility,
      formatDate,
      disabledDate,
      handleEventChange,
      handleRefresh,
      handleDateRangeChange,
      handleCalculate,
      handleReportVisibilityChange,
      goToEditEvent,
      getEventStatusText,
      filterOption,
      reportApi: api,
    };
  },
};
</script>

<style scoped>
.event-report-tab {
  padding: 20px 0;
  background: #fff;
  min-height: 400px;
}

/* 이벤트 선택 패널 */
.event-selector-panel {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.event-selector-content {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.selector-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.selector-column {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 300px;
}

.selector-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
}

.filter-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 8px;
}

.event-id-copy {
  margin-left: 8px;
}

.event-id-text {
  color: #666;
  font-size: 12px;
  font-weight: normal;
}

.event-selector {
  min-width: 500px;
  max-width: 700px;
  flex: 1;
}

.date-range-picker {
  width: 260px;
}

.selector-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

/* 컨트롤 패널 */
.control-panel {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
}

.control-section {
  flex: 1;
  min-width: 300px;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 12px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid #d1d5db;
}

.section-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

/* 선택된 이벤트 정보 표시 */
.selected-event-info {
  flex: 1;
  min-width: 300px;
  padding: 8px 12px;
  background: #f0f5ff;
  border: 1px solid #d6e4ff;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.selected-event-title {
  font-weight: 600;
  color: #1890ff;
}

.selected-event-period {
  color: #6b7280;
  font-size: 13px;
}

.no-event-hint {
  flex: 1;
  min-width: 300px;
  padding: 8px 12px;
  background: #fafafa;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  color: #8c8c8c;
  font-style: italic;
}

/* 설정 섹션 */
.settings-section {
  min-width: 200px;
  flex: 0 0 auto;
}

.settings-section .section-title {
  display: flex;
  align-items: center;
  gap: 6px;
}

.help-icon {
  font-size: 14px;
  color: #8c8c8c;
  cursor: help;
}

.visibility-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.visibility-status {
  font-size: 14px;
  font-weight: 500;
}

.status-active {
  color: #52c41a;
}

.status-inactive {
  color: #8c8c8c;
}

.visibility-description {
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.5;
}

.desc-active {
  color: #52c41a;
}

.desc-inactive {
  color: #8c8c8c;
}

.desc-disabled {
  color: #bfbfbf;
  font-style: italic;
}

/* 이벤트 미선택 안내 */
.no-event-selected,
.no-data {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  background: #fafafa;
  border: 2px dashed #d9d9d9;
  border-radius: 12px;
}

.no-event-content,
.no-data-content {
  text-align: center;
  padding: 40px;
}

.no-event-icon,
.no-data-icon {
  font-size: 48px;
  display: block;
  margin-bottom: 16px;
}

.no-event-content h3,
.no-data-content h3 {
  font-size: 20px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 8px 0;
}

.no-event-content p,
.no-data-content p {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

.hint-text {
  margin-top: 12px !important;
  color: #1890ff !important;
  font-weight: 500;
}

/* 로딩 컨테이너 */
.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

/* 조회 결과 */
.results-section {
  background: #fff;
}

:deep(.ant-tabs-card > .ant-tabs-nav .ant-tabs-tab) {
  padding: 12px 20px;
  font-weight: 500;
}

:deep(.ant-tabs-card > .ant-tabs-nav .ant-tabs-tab-active) {
  border-bottom-color: #fff;
}

:deep(.ant-tabs-content) {
  background: #fff;
}

/* 그룹별 분석 하위 탭 */
.group-analysis-sub-tabs {
  margin-top: 0;
  padding-top: 16px;
}

:deep(.group-analysis-sub-tabs .ant-tabs-nav) {
  margin-bottom: 20px;
  background: #fafafa;
  padding: 12px 16px 0;
  border-radius: 8px 8px 0 0;
}

:deep(.group-analysis-sub-tabs .ant-tabs-tab) {
  padding: 10px 20px;
  font-weight: 500;
}

:deep(.group-analysis-sub-tabs .ant-tabs-tab-active) {
  background: #fff;
  border-radius: 6px 6px 0 0;
}

/* 빈 콘텐츠 영역 */
.empty-content {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  background: #fafafa;
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
  margin: 20px;
}

.empty-content p {
  font-size: 16px;
  color: #8c8c8c;
  margin: 0;
}
</style>
